import { LoanInvestmentSettings } from "../types";

export const calculateLoanAmounts = (loanInvestmentSettings: LoanInvestmentSettings) => {
    // Main Property Loan Principal (P_main)
    const mainPropertyLoanPrincipal = Math.max(0, loanInvestmentSettings.propertyPrice - loanInvestmentSettings.equity);
    
    // CapEx Loan Principal (P_capex)
    const capexLoanPrincipal = loanInvestmentSettings.improvementLoan;
    
    // Total Initial Loan Principal for Amortization (P_total_amortize)
    const totalLoanPrincipal = mainPropertyLoanPrincipal + capexLoanPrincipal + loanInvestmentSettings.establishmentCosts;

    return {
        mainPropertyLoanPrincipal,
        capexLoanPrincipal,
        totalLoanPrincipal
    };
};

export const calculateMonthlyPayment = (loanInvestmentSettings: LoanInvestmentSettings, totalLoanPrincipal: number) => {
    // Annual Interest Rate (r)
    const annualInterestRate = loanInvestmentSettings.annualInterestRate / 100;
    
    // Loan Term (Months): N = Løbetid * 12
    const loanTermInMonths = loanInvestmentSettings.loanTerm * 12;
    
    // Grace Period (Months): G = Afdragsfrihed * 12
    const gracePeriodInMonths = loanInvestmentSettings.interestOnlyPeriod * 12;
    
    // Amortization Period (Months): A = N - G
    const amortizationPeriodInMonths = loanTermInMonths - gracePeriodInMonths;
    
    // Monthly Interest Rate (i): i = r / 12
    const monthlyInterestRate = annualInterestRate / 12;

    // If no amortization period, return only interest payment
    if (amortizationPeriodInMonths <= 0) {
        return totalLoanPrincipal * monthlyInterestRate;
    }

    // Monthly Payment (Y) using annuity formula
    // Y = P_total_amortize * [i * (1 + i)^A] / [(1 + i)^A - 1]
    return (totalLoanPrincipal *
        (monthlyInterestRate * (1 + monthlyInterestRate) ** amortizationPeriodInMonths)) /
        ((1 + monthlyInterestRate) ** amortizationPeriodInMonths - 1);
};

export const generateAmortizationSchedule = (loanInvestmentSettings: LoanInvestmentSettings) => {
    const { mainPropertyLoanPrincipal, capexLoanPrincipal, totalLoanPrincipal } = calculateLoanAmounts(loanInvestmentSettings);
    const monthlyPayment = calculateMonthlyPayment(loanInvestmentSettings, totalLoanPrincipal);
    const monthlyInterestRate = (loanInvestmentSettings.annualInterestRate / 100) / 12;
    
    // Loan Term (Months): N = Løbetid * 12
    const loanTermInMonths = loanInvestmentSettings.loanTerm * 12;
    
    // Grace Period (Months): G = Afdragsfrihed * 12
    const gracePeriodInMonths = loanInvestmentSettings.interestOnlyPeriod * 12;
    
    // Calculate ratios for proportional amortization
    const totalInitialLoans = mainPropertyLoanPrincipal + capexLoanPrincipal;
    const mainLoanRatio = mainPropertyLoanPrincipal / totalInitialLoans;
    const capexLoanRatio = capexLoanPrincipal / totalInitialLoans;

    let remainingMainPrincipal = mainPropertyLoanPrincipal;
    let remainingCapexPrincipal = capexLoanPrincipal;
    let remainingTotalPrincipal = totalLoanPrincipal;

    const schedule = Array.from({ length: loanTermInMonths + 1 }, (_, month) => {
        if (month === 0) {
            return {
                mainPrincipal: mainPropertyLoanPrincipal,
                capexPrincipal: capexLoanPrincipal,
                totalPrincipal: totalLoanPrincipal
            };
        }

        // Calculate Interest for the month: Interest_total = Remaining_Principal_total_at_start_of_month * i
        const monthlyInterest = remainingTotalPrincipal * monthlyInterestRate;
        let principalPayment = 0;

        if (month <= gracePeriodInMonths) {
            // Within grace period: Monthly Payment = Interest_total, Principal Paid = 0
            principalPayment = 0;
        } else {
            // Within amortization period: Principal Paid = Y - Interest_total
            principalPayment = monthlyPayment - monthlyInterest;
        }

        // Distribute Afdrag to Main and CapEx loans proportionally
        const mainPrincipalPayment = principalPayment * mainLoanRatio;
        const capexPrincipalPayment = principalPayment * capexLoanRatio;

        // Update Remaining Principal for each loan
        remainingMainPrincipal = Math.max(0, remainingMainPrincipal - mainPrincipalPayment);
        remainingCapexPrincipal = Math.max(0, remainingCapexPrincipal - capexPrincipalPayment);
        remainingTotalPrincipal = remainingMainPrincipal + remainingCapexPrincipal;

        return {
            mainPrincipal: remainingMainPrincipal,
            capexPrincipal: remainingCapexPrincipal,
            totalPrincipal: remainingTotalPrincipal
        };
    });

    return schedule;
};
